'use client'

import { useState, useEffect } from 'react'
import AppIcon from '@/components/content/app/AppIcon'
import Pill from '@/components/ui/Pill'
import Link from '@/components/ui/Link'
import Menu from '@/components/ui/Menu'
import Breadcrumb from '@/components/content/sidebar/Breadcrumb'
import { createLogger } from '@/lib/logger'
import { getBrandColors, cn } from '@/lib/utils'
import { generateMenu, type MenuItem } from '@/helpers/menus'
import { queryPayload } from '@/lib/payloadClient'
import { determineDealsToDisplay } from '@/components/content/deals/helpers'
import type { App, Course } from '@/payload-types'

const logger = createLogger('CourseSidebar')

type CoursesSidebarProps = {
  course: Course
  app: App
  video?: boolean
  price?: string | null
  customLink?: string
  hideBreadcrumb?: boolean
}

export default function CoursesSidebar({
  course,
  app,
  video,
  price,
  customLink,
  hideBreadcrumb,
}: CoursesSidebarProps) {
  const [hasAccess, setHasAccess] = useState(false)
  const [menu, setMenu] = useState<MenuItem[]>([])

  // Fetch deals client-side for menu generation
  useEffect(() => {
    async function fetchDeals() {
      try {
        const { docs: deals } = await queryPayload({
          collection: 'deal',
          select: {
            name: true,
            unitType: true,
            isReward: true,
            durationDays: true,
            quantity: true,
            order: true,
            featuredOrder: true,
            startDate: true,
            endDate: true,
            product: true,
          },
          where: {
            app: { equals: app.id },
          },
          // Shape-suffixed cacheKey: shares the `withProduct` shape with
          // AppOverview.tsx but distinct from Ad.tsx's `base` shape. See
          // plan/KNOWN_ISSUES.md §2.
          cacheKey: `query:deal:app:${app.id}:withProduct`,
        })

        const dealsToDisplay = determineDealsToDisplay(deals)

        const m = generateMenu('app', {
          app,
          deals: dealsToDisplay,
          video: video,
          course: `/courses/${app.slug}/access`,
        })

        setMenu(m)
      } catch (error) {
        logger.error('Failed to fetch deals:', error)
      }
    }
    fetchDeals()
  }, [app])

  useEffect(() => {
    async function fetchAndSetHasCourseAccess() {
      try {
        const res = await fetch(`/api/courses/check-access?courseId=${course.id}`)
        if (!res.ok) {
          logger.error(`Course access check failed with status ${res.status}`)
          return
        }
        const data = await res.json()
        setHasAccess(data.hasAccess === true)
      } catch (error) {
        logger.error('Failed to check course access:', error)
      }
    }
    fetchAndSetHasCourseAccess()
  }, [course.id])

  const { backgroundColor } = getBrandColors(app)

  return (
    <div
      className={cn(
        'flex w-full flex-row items-center gap-3.5 lg:flex-row lg:items-center',
        hideBreadcrumb ? 'p-4' : 'md:flex-col md:items-start md:p-4',
      )}
    >
      <div className="flex min-w-0 flex-1 items-center gap-3.5 lg:w-[calc(100%-124px)]">
        <div className={cn('shrink-0 md:h-14 md:w-14', hideBreadcrumb ? 'h-14 w-14' : 'h-8 w-8')}>
          <AppIcon icon={app.icon} name={app.name} slug={app.slug} background={backgroundColor} />
        </div>
        <div
          className={cn(
            'gap-0.5 leading-tight lg:w-[calc(100%-70px)]',
            hideBreadcrumb ? 'flex w-full' : 'hidden flex-col md:mt-1 md:flex md:w-full',
          )}
        >
          <span className="truncate font-semibold text-gray-950 dark:text-white">
            {course.name}
          </span>
          {price && (
            <span className="truncate text-13 text-gray-600 dark:text-zinc-500">{price}</span>
          )}
        </div>
        {!hideBreadcrumb && <Breadcrumb placeholder="Course" title={course.name} />}
      </div>
      <div className="flex shrink-0 gap-2.5 lg:ml-auto">
        {customLink || hasAccess ? (
          <Link
            href={hasAccess ? `/courses/${app.slug}/access` : customLink!}
            target={hideBreadcrumb || hasAccess ? '_self' : '_blank'}
          >
            <Pill style="primary" as="span">
              <span className="md:hidden">{hasAccess ? 'Access' : 'Buy'}</span>
              <span className="hidden md:inline">
                {hasAccess
                  ? hideBreadcrumb
                    ? 'Access'
                    : 'Access Course'
                  : hideBreadcrumb
                    ? 'Buy'
                    : 'Buy Course'}
              </span>
            </Pill>
          </Link>
        ) : (
          <Pill style="disabled">
            <span className="md:hidden">Buy</span>
            <span className="hidden md:inline">Buy Course</span>
          </Pill>
        )}
        {menu.length > 0 && <Menu items={menu} align="right" subAlign="down" />}
      </div>
    </div>
  )
}
